import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fmtHours, localHM } from "@vimaana/ui";
import { useAktivitaet, useRejectSlot, useUpdateSlot } from "../../hooks/usePlanung";
import type { AktivitaetItem } from "../../hooks/usePlanung";
import { errorText } from "../../lib/invoices";
import { btnGhost, btnPrimary } from "./planer-styles";

/**
 * Was aus der Team-App hereinkommt und auf eine Entscheidung wartet.
 *
 * Zwei Arten, die verschiedene Knoepfe brauchen: eine **Bewerbung** auf eine
 * offene Stelle wird angenommen oder mit Begruendung abgelehnt (die Begruendung
 * liest der Helfer in seiner App — eine Absage ohne Wort ist keine Antwort).
 * Eine **Zeitaenderung** wird bestaetigt oder an den Helfer zurueckgegeben.
 *
 * Das Panel liegt an der Planung statt auf einer eigenen Seite: die Entscheidung
 * gehoert neben den Kalender, in dem sie wirkt.
 */
export function AktivitaetPanel({ onClose }: { onClose: () => void }) {
  const { data, isLoading, isError } = useAktivitaet();
  const qc = useQueryClient();
  // Die Aktivitaet haengt an keinem einzelnen Projekt — der Slot-Invalidierer
  // will trotzdem einen Schluessel. "planung" zieht den Kalender nach, was hier
  // genau richtig ist: eine angenommene Bewerbung aendert die Besetzung.
  const updateSlot = useUpdateSlot("planung");
  const rejectSlot = useRejectSlot("planung");
  const nachziehen = () => qc.invalidateQueries({ queryKey: ["kontor", "aktivitaet"] });
  const busy = updateSlot.isPending || rejectSlot.isPending;
  const fehler = updateSlot.error ?? rejectSlot.error;

  const [absageFuer, setAbsageFuer] = useState<string | null>(null);
  const [absageText, setAbsageText] = useState("");

  const setStatus = (slotId: string, confirm_status: "bestaetigt" | "geplant") =>
    updateSlot.mutate({ slotId, patch: { confirm_status } }, { onSuccess: nachziehen });

  const bewerbungAnnehmen = (slotId: string) =>
    updateSlot.mutate({ slotId, patch: { einladungs_status: "zugesagt" } }, { onSuccess: nachziehen });

  const absageSenden = (slotId: string) =>
    rejectSlot.mutate(
      { slotId, kommentar: absageText.trim() },
      { onSuccess: () => { setAbsageFuer(null); setAbsageText(""); nachziehen(); } },
    );

  return (
    <div className="pl-overlay" onClick={onClose}>
      <div className="pl-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="pl-kopf">
          <div>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>Aktivität</h2>
            <div style={{ color: "var(--vg-text-3)", fontSize: 13, marginTop: 2 }}>
              Was aus der Team-App hereinkommt
            </div>
          </div>
          <button className="pl-closebtn" type="button" onClick={onClose} aria-label="Schließen">×</button>
        </div>

        <div style={{ padding: "16px 20px 40px", display: "flex", flexDirection: "column", gap: 18 }}>
          {isError && <div style={{ color: "var(--vg-red)" }}>Aktivität konnte nicht geladen werden.</div>}
          {isLoading && <div style={{ color: "var(--vg-text-3)" }}>lädt …</div>}
          {fehler != null && (
            <div style={{ color: "var(--vg-red)", fontSize: 13 }}>{errorText(fehler)}</div>
          )}

          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <h3 style={abschnitt}>Braucht deine Entscheidung</h3>
            {data && data.offen.length === 0 && (
              <div style={leise}>Nichts offen — alles entschieden.</div>
            )}
            {data?.offen.map((it) => {
              const bewerbung = it.art === "bewerbung";
              return (
                <article key={it.slot_id} style={karteOffen}>
                  <Kopf it={it} marke={bewerbung ? "Bewerbung" : "Änderung"} />
                  <div style={{ ...kasten, borderColor: bewerbung ? "var(--vg-blue-line)" : "var(--vg-amber-line)", background: bewerbung ? "var(--vg-blue-soft)" : "var(--vg-amber-soft)", color: bewerbung ? "var(--k-on-blue-soft)" : "var(--vg-text)" }}>
                    {bewerbung
                      ? (it.text || "Möchte auf der offenen Stelle mitarbeiten")
                      : it.text
                        ? <><b>Geändert:</b> {it.text}</>
                        : <>Zeiten geändert, kein Vorher-Wert protokolliert. Neu: {localHM(it.start_at)}–{localHM(it.end_at)}</>}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      style={{ ...btnPrimary, minHeight: 44 }}
                      disabled={busy}
                      onClick={() => (bewerbung ? bewerbungAnnehmen(it.slot_id) : setStatus(it.slot_id, "bestaetigt"))}
                    >
                      ✓ {bewerbung ? "Annehmen" : "Bestätigen"}
                    </button>
                    <button
                      type="button"
                      style={{ ...btnGhost, minHeight: 44 }}
                      disabled={busy}
                      onClick={() => {
                        if (!bewerbung) { setStatus(it.slot_id, "geplant"); return; }
                        setAbsageFuer(absageFuer === it.slot_id ? null : it.slot_id);
                        setAbsageText("");
                      }}
                    >
                      {bewerbung ? "Ablehnen" : "Zurück an Helfer"}
                    </button>
                  </div>

                  {bewerbung && absageFuer === it.slot_id && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <textarea
                        value={absageText}
                        onChange={(e) => setAbsageText(e.target.value)}
                        placeholder={`Kurze Begründung für ${it.person_name} — sie steht so in der Team-App`}
                        rows={2}
                        autoFocus
                        style={absageFeld}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="button" style={{ ...btnGhost, minHeight: 44, color: "var(--vg-red)", borderColor: "var(--vg-red-line)", background: "var(--vg-red-soft)" }} disabled={busy} onClick={() => absageSenden(it.slot_id)}>
                          Ablehnung senden
                        </button>
                        <button type="button" style={{ ...btnGhost, minHeight: 44 }} disabled={busy} onClick={() => { setAbsageFuer(null); setAbsageText(""); }}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <h3 style={abschnitt}>Zuletzt aus dem Team</h3>
            {data && data.verlauf.length === 0 && <div style={leise}>Noch keine Einträge.</div>}
            {data?.verlauf.map((it) => (
              <div key={it.slot_id + it.zeitpunkt} style={karteFertig}>
                <Kopf it={it} marke={kindLabel(it.kind)} />
                {it.text && <div style={{ fontSize: 13, color: "var(--vg-text-2)" }}>{it.text}</div>}
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}

function Kopf({ it, marke }: { it: AktivitaetItem; marke: string }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700 }}>
          {it.person_name}
          <span style={marker}>{marke}</span>
        </span>
        <span style={{ fontSize: 12, color: "var(--vg-text-3)" }}>{fmtTag(it.datum)}</span>
      </div>
      <div style={{ fontSize: 13, color: "var(--vg-text-2)" }}>
        {it.projekt_title || "Ohne Projekt"} · {localHM(it.start_at)}–{localHM(it.end_at)} · {fmtHours(it.stunden_netto)}
      </div>
    </>
  );
}

function kindLabel(kind: string): string {
  if (kind === "bestaetigt") return "Bestätigt";
  if (kind === "kommentar") return "Kommentar";
  return "Änderung";
}

/** „2026-08-28" → „Fr, 28.08." — der Wochentag sagt hier mehr als das Jahr. */
function fmtTag(datum: string): string {
  if (!datum) return "";
  const d = new Date(`${datum}T12:00:00`);
  if (Number.isNaN(d.getTime())) return datum;
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
}

const abschnitt = { margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" as const, color: "var(--vg-text-3)" };
const leise = { fontSize: 13, color: "var(--vg-text-3)" };
const karteOffen = { display: "flex", flexDirection: "column" as const, gap: 8, border: "1px solid var(--vg-line-2)", borderRadius: 12, padding: "12px 14px", background: "var(--vg-bg-2)" };
const karteFertig = { display: "flex", flexDirection: "column" as const, gap: 4, border: "1px solid var(--vg-line)", borderRadius: 12, padding: "10px 12px", background: "var(--vg-bg-2)" };
const kasten = { border: "1px solid", borderRadius: 9, padding: "8px 10px", fontSize: 13 };
const marker = { padding: "1px 8px", borderRadius: 999, border: "1px solid var(--vg-line-2)", background: "var(--vg-bg-3)", color: "var(--vg-text-3)", fontSize: 11, fontWeight: 600 };
const absageFeld = { boxSizing: "border-box" as const, width: "100%", padding: "8px 10px", borderRadius: 9, border: "1px solid var(--vg-line-2)", background: "var(--vg-bg-2)", color: "var(--vg-text)", fontFamily: "inherit", fontSize: 16, resize: "vertical" as const };
